/**
 * ViewReasonModal — 개인정보(사진) 열람 사유 입력 모달
 *
 * 관리자가 사진을 열람하기 전에 사유를 입력받아 audit_logs에 기록한다.
 * 사유 2자 미만이면 [열람] 비활성.
 */

import { useEffect, useState } from 'react'
import { Modal } from './Modal'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'
import styles from './ViewReasonModal.module.css'

export interface ViewReasonModalProps {
  /** 모달 열림 여부 */
  open: boolean
  /** 닫기 요청 콜백 */
  onClose: () => void
  /** 사유 입력 후 [열람] 확정 콜백 (trim된 사유 전달) */
  onConfirm: (reason: string) => void
  /** 헤더 타이틀. 기본값 '열람 사유 입력' */
  title?: string
  /** 본문 안내 문구 */
  description?: string
  /** 열람 처리 중 (버튼 로딩/비활성) */
  loading?: boolean
  /** 열람 실패 시 에러 메시지 */
  error?: string | null
}

export function ViewReasonModal({
  open,
  onClose,
  onConfirm,
  title = '열람 사유 입력',
  description,
  loading = false,
  error,
}: ViewReasonModalProps) {
  const [reason, setReason] = useState('')

  // 모달이 닫히면 입력값 초기화
  useEffect(() => {
    if (!open) setReason('')
  }, [open])

  const trimmed = reason.trim()
  const canConfirm = trimmed.length >= 2 && !loading

  return (
    <Modal open={open} onClose={onClose} title={title} size="sm">
      <div className={styles.body}>
        <p className={styles.desc}>
          {description ?? '개인정보(사진) 열람은 접속기록으로 남습니다. 열람 사유를 입력해 주세요.'}
        </p>
        <Input
          size="md"
          autoFocus
          placeholder="예: CS 문의 대응, 부적절 콘텐츠 점검"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canConfirm) onConfirm(trimmed)
          }}
        />
        {error && <Alert variant="danger">{error}</Alert>}
        <div className={styles.actions}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={loading}>
            취소
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onConfirm(trimmed)}
            disabled={!canConfirm}
            loading={loading}
          >
            열람
          </Button>
        </div>
      </div>
    </Modal>
  )
}
